import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { InitEventApiService } from 'ngx-mt-ui-lib/api-client';
import { EventListActions } from './event-list.actions';
import { ToastService } from 'ngx-mt-ui-lib/services/toast';
import { TranslateService } from '@ngx-translate/core';

const EVENT_LIST_LOAD_ERROR = 'EVENT_LIST.ERRORS.LOAD';
const EVENT_LIST_NETWORK_ERROR = 'EVENT_LIST.ERRORS.NETWORK';
const EVENT_LIST_HTTP_ERROR = 'EVENT_LIST.ERRORS.HTTP_STATUS';
const EVENT_LIST_UNKNOWN_ERROR = 'EVENT_LIST.ERRORS.UNKNOWN';

@Injectable()
export class EventListEffects {
  private readonly actions$ = inject(Actions);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly initEventApi = inject(InitEventApiService);
  private requestErrorNotified = false;

  readonly requestEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventListActions.requestEvents),
      switchMap(() =>
        this.initEventApi.postMapLayer({}).pipe(
          map((stations) =>
            EventListActions.requestEventsSuccess({
              stations,
            }),
          ),
          catchError((error) =>
            of(EventListActions.requestEventsError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestEventsNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EventListActions.requestEventsError,
          EventListActions.requestEventsSuccess,
        ),
        tap((action) => {
          if (action.type === EventListActions.requestEventsSuccess.type) {
            this.requestErrorNotified = false;
            return;
          }

          if (action.error?.status === 401 || this.requestErrorNotified) {
            return;
          }

          this.requestErrorNotified = true;
          this.toastService.add({
            level: 'error',
            summary: EVENT_LIST_LOAD_ERROR,
            detail: this.getRequestErrorDetail(action.error),
          });
        }),
      ),
    { dispatch: false },
  );

  private getRequestErrorDetail(
    error: { status?: number } | null | undefined,
  ): string {
    const status = error?.status;

    if (status === 0) {
      return this.translateService.instant(EVENT_LIST_NETWORK_ERROR);
    }

    if (typeof status === 'number' && status > 0) {
      return this.translateService.instant(EVENT_LIST_HTTP_ERROR, {
        status,
      });
    }

    return this.translateService.instant(EVENT_LIST_UNKNOWN_ERROR);
  }
}
